import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/adminService';

const FEATURE_KEYS = [
  { key: 'zimbabwe_geofencing_enabled', label: 'Zimbabwe Geofencing', desc: 'Restrict access to users within Zimbabwe' },
  { key: 'chat_contact_blocking_enabled', label: 'Chat Contact Blocking', desc: 'Block contact info in chat before deal confirmation' },
  { key: 'otp_verification_required', label: 'OTP Verification Required', desc: 'Require phone OTP to complete registration' },
  { key: 'surveyor_auto_assign_enabled', label: 'Auto-Assign Surveyors', desc: 'Automatically assign surveyors based on service type' },
];

export const AdminSettings: React.FC = () => {
  const [settings, setSettings] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    adminService.getSettings().then(setSettings).catch(() => {}).finally(() => setIsLoading(false));
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      const updated = await adminService.updateSettings(settings);
      setSettings(updated);
      setMessage('✅ Settings saved successfully.');
    } catch (e: any) {
      setMessage(`❌ ${e?.message || 'Failed to save settings'}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <div style={s.center}><div style={s.spinner} /></div>;

  const commissionRate = parseFloat(settings.commission_rate ?? '0.10');
  const isMessageSuccess = message.startsWith('✅');

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '0 8px' }}>
      <h1 style={s.title}>Platform Settings</h1>
      <p style={s.subtitle}>Configure commission rates and feature toggles.</p>

      {message && (
        <div style={{
          padding: '12px 16px',
          borderRadius: 10,
          background: isMessageSuccess ? 'var(--sc-success-bg)' : 'var(--sc-error-bg)',
          color: isMessageSuccess ? 'var(--sc-success-text)' : 'var(--sc-error-text)',
          marginBottom: 20,
          fontSize: 13,
          fontWeight: 500,
        }}>
          {message}
        </div>
      )}

      {/* Commission Rate */}
      <div style={s.card}>
        <h3 style={s.cardTitle}>Commission Rate</h3>
        <p style={{ fontSize: 13, color: 'var(--sc-text-secondary)', marginBottom: 16 }}>
          Percentage of deal value taken as platform commission.
        </p>
        <div style={s.rateRow}>
          <input
            type="range" min={1} max={30} step={1}
            value={commissionRate * 100}
            onChange={(e) => setSettings({ ...settings, commission_rate: parseInt(e.target.value) / 100 })}
            style={{ flex: 1, accentColor: 'var(--sc-brand-primary)' }}
          />
          <div style={s.rateDisplay}>{(commissionRate * 100).toFixed(0)}%</div>
          <input
            type="number" min={1} max={30} step={1}
            value={(commissionRate * 100).toFixed(0)}
            onChange={(e) => setSettings({ ...settings, commission_rate: parseInt(e.target.value) / 100 })}
            style={s.rateInput}
          />
        </div>
        <p style={{ fontSize: 12, color: 'var(--sc-text-tertiary)', marginTop: 8 }}>
          On a $1,000 deal: platform earns ${(commissionRate * 1000).toFixed(2)}, surveyor earns ${((1 - commissionRate) * 1000).toFixed(2)}.
        </p>
      </div>

      {/* Feature Toggles */}
      <div style={{ ...s.card, marginTop: 20 }}>
        <h3 style={s.cardTitle}>Feature Toggles</h3>
        {FEATURE_KEYS.map((f) => (
          <div key={f.key} style={s.toggleRow}>
            <div style={{ flex: 1, paddingRight: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--sc-text-primary)' }}>{f.label}</div>
              <div style={{ fontSize: 12, color: 'var(--sc-text-secondary)', marginTop: 2 }}>{f.desc}</div>
            </div>
            <button
              style={{
                ...s.toggleBtn,
                background: settings[f.key] ? 'var(--sc-brand-primary)' : 'var(--sc-border)',
              }}
              onClick={() => setSettings({ ...settings, [f.key]: !settings[f.key] })}
            >
              <div style={{
                ...s.toggleKnob,
                transform: settings[f.key] ? 'translateX(22px)' : 'translateX(2px)',
                background: settings[f.key] ? 'var(--sc-text-inverse)' : 'var(--sc-text-tertiary)',
              }} />
            </button>
          </div>
        ))}
      </div>

      <button style={s.saveBtn} disabled={isSaving} onClick={handleSave}>
        {isSaving ? 'Saving…' : '💾 Save Settings'}
      </button>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  center: { display: 'flex', justifyContent: 'center', padding: 60 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  title: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', fontFamily: "'Outfit', sans-serif" },
  subtitle: { fontSize: 14, color: 'var(--sc-text-secondary)', margin: '0 0 24px' },
  card: { background: 'var(--sc-bg-secondary)', borderRadius: 14, padding: 24, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-md)' },
  cardTitle: { fontSize: 16, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '0 0 12px', fontFamily: "'Outfit', sans-serif" },
  rateRow: { display: 'flex', alignItems: 'center', gap: 16 },
  rateDisplay: { fontSize: 28, fontWeight: 800, color: 'var(--sc-brand-primary)', minWidth: 60, textAlign: 'center' as const, fontFamily: "'Outfit', sans-serif" },
  rateInput: { width: 70, padding: '8px 12px', border: '1px solid var(--sc-border)', borderRadius: 8, fontSize: 16, textAlign: 'center' as const, outline: 'none', background: 'var(--sc-bg-input)', color: 'var(--sc-text-primary)' },
  toggleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid var(--sc-border)' },
  toggleBtn: { width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', padding: 0 },
  toggleKnob: { position: 'absolute', top: 3, width: 20, height: 20, borderRadius: '50%', transition: 'transform 0.2s' },
  saveBtn: { marginTop: 24, padding: '14px 32px', background: 'var(--sc-brand-primary)', color: 'var(--sc-text-inverse)', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', width: '100%', minHeight: 48 },
};
